/**
 * SA 担当顧客の詳細ページ (2026-08-06)。
 *
 * URL: /sa/contacts/:contactId
 *
 * 一覧 (/sa/contacts) の各行「顧客詳細を開く →」から遷移する。
 * 社内向け顧客詳細 (/sales/contacts/:contactId) は owner/editor 前提の
 * サブコレクション (field_changes / referral_records) を読むため SA では使えない。
 * ここでは rules で SA が read できる範囲 (自分が ownerSaUid の mail_contacts +
 * deals) だけを表示する読み取り専用ビュー。
 *
 * 2026-10-30: 商談の行は戻り先 (顧客詳細) を渡す。商談詳細の「前の画面へ」でここへ戻れる。
 */
import {
  SA_RETENTION_DEFAULT_REMINDER_DAYS,
  resolveRetentionState,
  type IsoOrTimestamp,
} from '@goenhub/shared';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  CalendarClock,
  Mail,
  MapPin,
  Phone,
  StickyNote,
  UserCircle2,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import { useDeals } from '@/hooks/useDeals';
import { useMailContact } from '@/hooks/useMailContacts';
import { useReturnToState } from '@/hooks/useReturnTo';
import { useSalesSettingsWithDefaults } from '@/hooks/useSettings';
import { toDate } from '@/lib/firestoreTime';
import { paths } from '@/routes/paths';

const STAGE_LABEL: Record<string, string> = {
  document_requested: '資料請求',
  briefing: '個別説明',
  quote: '見積',
  won: '成約',
  agreed: '同意',
  paid: '入金',
  ordered: '発注',
  delivered: '納品',
  lost: '失注',
};

function fmtDate(v: IsoOrTimestamp | Date | null | undefined): string {
  const d = v instanceof Date ? v : toDate(v);
  return d ? d.toLocaleDateString('ja-JP') : '—';
}

export function SaContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const { user } = useAuth();
  const { data: contact, isLoading, error } = useMailContact(contactId);
  const dealsQ = useDeals({ contactId });
  const { merged: salesSettings } = useSalesSettingsWithDefaults();
  const returnToState = useReturnToState('顧客詳細');
  const reminderDays =
    salesSettings.saRetentionReminderDays ?? SA_RETENTION_DEFAULT_REMINDER_DAYS;

  // 自分が関わる商談のみ (一覧の「担当商談」と同じ基準)
  const myDeals = useMemo(() => {
    const uid = user?.uid;
    if (!uid) return [];
    return (dealsQ.data ?? []).filter(
      (d) => d.assigneeUid === uid || d.salesAgentUid === uid,
    );
  }, [dealsQ.data, user?.uid]);

  if (isLoading) {
    return (
      <Screen title="顧客詳細" backTo={paths.saContacts}>
        <Spinner />
      </Screen>
    );
  }

  // rules 上、自分が ownerSaUid でない顧客は read できず error になる
  if (error || !contact) {
    return (
      <Screen title="顧客詳細" backTo={paths.saContacts}>
        <div className="space-y-2">
          <p className="text-sm text-[var(--accent-red)]">
            顧客が見つかりませんでした。担当から外れている可能性があります。
          </p>
          <Link
            to={paths.saContacts}
            className="text-xs text-[var(--accent)] hover:underline inline-flex items-center gap-1"
          >
            <ArrowLeft className="size-3" />
            自分の顧客に戻る
          </Link>
        </div>
      </Screen>
    );
  }

  const isBtoB = contact.kind === 'btob';
  const until = toDate(contact.saRetentionUntil);
  const { state, daysLeft } = resolveRetentionState({
    hasOwnerSa: !!contact.ownerSaUid,
    until,
    now: new Date(),
    reminderDays,
  });

  const addr = contact.personalAddress;
  const addressLine =
    [
      addr?.postalCode ? `〒${addr.postalCode}` : '',
      addr?.prefecture ?? '',
      addr?.city ?? '',
      addr?.line ?? '',
    ]
      .filter(Boolean)
      .join(' ') ||
    [
      contact.postalCode ? `〒${contact.postalCode}` : '',
      contact.prefecture ?? '',
      contact.address ?? '',
      contact.buildingName ?? '',
    ]
      .filter(Boolean)
      .join(' ');

  const rows: Array<{ label: string; value: string }> = [
    { label: 'メールアドレス', value: contact.defaultEmail || '—' },
    { label: '電話番号', value: contact.phone || '—' },
    {
      label: 'フリガナ',
      value:
        [contact.lastNameKana, contact.firstNameKana].filter(Boolean).join(' ') ||
        '—',
    },
    ...(isBtoB
      ? [
          { label: '会社名', value: contact.companyName || '—' },
          { label: '部署 / 役職', value: [contact.department, contact.jobTitle].filter(Boolean).join(' / ') || '—' },
        ]
      : []),
    { label: '住所', value: addressLine || '—' },
    { label: '生年月日', value: contact.birthdate || '—' },
    { label: '職業', value: contact.occupation || '—' },
  ];

  return (
    <Screen title={contact.name || '(名前未設定)'} backTo={paths.saContacts} restoreScroll>
      {/* ヘッダーカード */}
      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {isBtoB ? (
                <Building2 className="size-4 text-[var(--accent)]" />
              ) : (
                <UserCircle2 className="size-4 text-[var(--accent-purple)]" />
              )}
              <h1 className="text-lg font-semibold text-[var(--text-primary)]">
                {contact.name || '(名前未設定)'}
              </h1>
              {isBtoB && <Badge variant="default">法人</Badge>}
              {contact.promotionLevel === 'customer' ? (
                <Badge variant="approved">顧客化</Badge>
              ) : contact.promotionLevel === 'inactive' ? (
                <Badge variant="default">休眠</Badge>
              ) : (
                <Badge variant="draft">リード</Badge>
              )}
              {state === 'expiring' && (
                <Badge variant="draft">期限間近 あと{daysLeft}日</Badge>
              )}
              {state === 'expired' && <Badge variant="danger">期限切れ</Badge>}
            </div>
            <div className="mt-2 flex items-center gap-4 flex-wrap text-xs text-[var(--text-secondary)]">
              <span className="inline-flex items-center gap-1.5 min-w-0">
                <Mail className="size-3 text-[var(--text-muted)]" />
                <span className="truncate">{contact.defaultEmail || '—'}</span>
              </span>
              {contact.phone && (
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="size-3 text-[var(--text-muted)]" />
                  {contact.phone}
                </span>
              )}
              {contact.companyName && (
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <Briefcase className="size-3 text-[var(--text-muted)]" />
                  <span className="truncate">{contact.companyName}</span>
                </span>
              )}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[10px] text-[var(--text-muted)] flex items-center gap-1 justify-end">
              <CalendarClock className="size-3" />
              顧客期限
            </div>
            <div
              className={`text-sm font-semibold tabular-nums ${
                state === 'expired'
                  ? 'text-[var(--accent-red)]'
                  : state === 'expiring'
                    ? 'text-[var(--accent-amber)]'
                    : 'text-[var(--text-secondary)]'
              }`}
            >
              {until ? fmtDate(until) : '無期限'}
            </div>
            {contact.saRetentionStartedAt && (
              <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                起算 {fmtDate(contact.saRetentionStartedAt)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 基本情報 */}
      <section className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 mb-4">
        <h3 className="text-xs font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-1.5">
          <MapPin className="size-3.5 text-[var(--text-muted)]" />
          基本情報
        </h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {rows.map((r) => (
            <div key={r.label} className="flex items-start gap-2 min-w-0">
              <dt className="w-24 shrink-0 text-[11px] text-[var(--text-muted)] pt-0.5">
                {r.label}
              </dt>
              <dd className="text-xs text-[var(--text-secondary)] break-words min-w-0">
                {r.value}
              </dd>
            </div>
          ))}
        </dl>
        {contact.salesMemo && (
          <div className="mt-4 pt-3 border-t border-[var(--border)]">
            <div className="text-[11px] text-[var(--text-muted)] flex items-center gap-1.5 mb-1">
              <StickyNote className="size-3" />
              営業メモ
            </div>
            <p className="text-xs text-[var(--text-secondary)] whitespace-pre-line">
              {contact.salesMemo}
            </p>
          </div>
        )}
      </section>

      {/* 関連商談 */}
      <section className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4">
        <h3 className="text-xs font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-1.5">
          <Briefcase className="size-3.5 text-[var(--text-muted)]" />
          この顧客の商談
          <span className="text-[10px] font-normal text-[var(--text-muted)]">
            ({myDeals.length}件)
          </span>
        </h3>
        {dealsQ.isLoading ? (
          <Spinner />
        ) : myDeals.length === 0 ? (
          <EmptyState
            icon={Briefcase}
            title="商談がまだありません"
            description="「担当商談」画面の「新規商談を起票」から作成できます。"
          />
        ) : (
          <div className="rounded-md border border-[var(--border)] overflow-hidden">
            {myDeals.map((d) => (
              <Link
                key={d.id}
                to={paths.dealDetail.replace(':id', d.id)}
                state={returnToState}
                className="flex items-center justify-between gap-3 px-3 py-2 text-xs border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)]"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate text-[var(--text-primary)]">
                    {d.title}
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] truncate">
                    最終更新 {fmtDate(d.lastTouchedAt)}
                    {d.primaryCategory ? ` / ${d.primaryCategory}` : ''}
                  </div>
                </div>
                <Badge
                  variant={
                    d.stage === 'won' || d.stage === 'paid' || d.stage === 'delivered'
                      ? 'approved'
                      : d.stage === 'lost'
                        ? 'default'
                        : 'review'
                  }
                >
                  {STAGE_LABEL[d.stage] ?? d.stage}
                </Badge>
                <div className="w-20 text-right tabular-nums text-[var(--text-secondary)] shrink-0">
                  {d.expectedAmount != null
                    ? `¥${d.expectedAmount.toLocaleString()}`
                    : '—'}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </Screen>
  );
}
